import * as React from 'react';
import { useDispatch } from 'react-redux';

import { alertSuccess } from '../../../store/reducers/Alert';
import { PDFNote } from '../models/PDFNote';
import { CommentButton } from "../../../components/buttons/CommentButton";
import { services } from '../../../services';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { ProjectDoc } from '../../projectDocs/models/ProjectDoc';
import { NewButton } from '../../../components/synced/ui/NewButton/NewButton';
import { buildContractorNotePermissions } from '../helpers/contractorNotePermissions';
import { ContractorViewerModal, isPdfAttachment, isWordAttachment } from './viewer/PDFViewerModal';
import { NoteUpdatesController } from '../helpers/noteUpdates';


interface NoteCardActionsProps {
  pdfNote: PDFNote;
  projectDocFolderStatusId: number;
  projectDoc?: ProjectDoc;
  onNoteChanged: () => void;
  noteUpdates?: NoteUpdatesController;
  noteUpdatesPending?: boolean;
}

export const NoteCardActions: React.FC<NoteCardActionsProps> = ({ pdfNote, projectDocFolderStatusId, projectDoc, onNoteChanged, noteUpdates, noteUpdatesPending }) => {
  // Правила видимости кнопок живут в одном месте — их же использует
  // сайдбар PDF-просмотрщика, чтобы наборы действий не разъехались.
  const permissions = React.useMemo(
    () => buildContractorNotePermissions(pdfNote, projectDocFolderStatusId, projectDoc),
    [pdfNote, projectDocFolderStatusId, projectDoc]
  );

  const dispatch = useDispatch();
  const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper<PDFNote>();
  const mutationPending = React.useRef(false);

  // Файл, к которому привязано замечание — по нему открывается просмотрщик.
  // Цепочка приезжает только если CWebApi её инклюдит (PDFNotesController).
  const noteAttachment = pdfNote.NoteWorkflowInstanceAttachments?.[0]
    ?.PDFWorkflowInstanceAttachment?.ProjectDocFolderAttachment;
  const canView = isPdfAttachment(noteAttachment) || isWordAttachment(noteAttachment);
  const [viewerIsOpen, setViewerIsOpen] = React.useState<boolean>(false);


  const onCancel = React.useCallback(() => {
    if (noteUpdates && (mutationPending.current || noteUpdatesPending || !pdfNote.Id)) return;
    if (noteUpdates) mutationPending.current = true;
    promiseWrapper(
      () => services.pDFNoteService.cancelCompletion(pdfNote.Id ?? 0),
      (response: PDFNote) => {
        mutationPending.current = false;
        if (!response)
          return;

        dispatch(alertSuccess());
        if (noteUpdates) noteUpdates.publish([pdfNote.Id ?? 0]);
        else onNoteChanged();
      },
      noteUpdates ? () => { mutationPending.current = false; } : undefined
    );
    // eslint-disable-next-line
  }, [pdfNote, dispatch, onNoteChanged, noteUpdates, noteUpdatesPending]);


  const onComplete = React.useCallback((comment: string) => {
    if (noteUpdates && (mutationPending.current || noteUpdatesPending || !pdfNote.Id)) return;
    if (noteUpdates) mutationPending.current = true;
    promiseWrapper(
      () => services.pDFNoteService.complete(pdfNote.Id ?? 0, comment),
      (response: PDFNote) => {
        mutationPending.current = false;
        if (!response)
          return;

        dispatch(alertSuccess());
        if (noteUpdates) noteUpdates.publish([pdfNote.Id ?? 0]);
        else onNoteChanged();
      },
      noteUpdates ? () => { mutationPending.current = false; } : undefined
    );
    // eslint-disable-next-line
  }, [pdfNote, dispatch, onNoteChanged, noteUpdates, noteUpdatesPending]);
  

  return (
    <React.Fragment>
      {viewerIsOpen && noteAttachment && (
        <ContractorViewerModal
          attachment={noteAttachment}
          projectDocFolderId={pdfNote.NoteObjectId}
          projectDocFolderStatusId={projectDocFolderStatusId}
          projectDoc={projectDoc}
          initialScrollToNoteId={pdfNote.Id}
          onHide={() => setViewerIsOpen(false)}
          onNotesChanged={onNoteChanged}
          {...(noteUpdates ? { noteUpdates } : {})} />
      )}
      {
        canView &&
        <NewButton
          variant="secondary"
          icon={isWordAttachment(noteAttachment) ? "fa-regular fa-file-word" : "fa-regular fa-file-pdf"}
          onClick={() => setViewerIsOpen(true)}>
          Посмотреть в документе
        </NewButton>
      }
      {
        permissions.canCancelCompletion &&
        <NewButton
          variant="danger"
          icon="fa-solid fa-xmark"
          disabled={noteUpdates ? isLoading || noteUpdatesPending === true : isLoading}
          onClick={onCancel}>
          Отменить выполнение
        </NewButton>
      }
      {
        permissions.canComplete &&
        <CommentButton
          className="ka-note-complete-btn p-button-sm"
          icon="fas fa-check"
          label="Выполнить"
          commentIsRequired={true}
          disabled={noteUpdates ? isLoading || noteUpdatesPending === true : isLoading}
          action={onComplete} />
      }
    </React.Fragment>
  );
}
